// src/hooks/useServicioFirma.js
//
// Estado del cliente de firma local (SIGIF Firma Local, 127.0.0.1:5850) y del token USB.
// Firmar se habilita solo con `listo`: el cliente responde y detecta el token. Consulta
// /status al montar y cada 5 s (el cliente lo resuelve por WMI, sin tocar el token, así
// que se puede consultar mientras firma).
import { useState, useEffect, useCallback } from 'react';

export const FIRMA_SERVICE_URL = process.env.REACT_APP_FIRMA_SERVICE_URL || 'http://127.0.0.1:5850';

const MENSAJES = {
    verificando: 'Verificando el cliente de firma…',
    sinServicio: 'SIGIF Firma Local no está abierto en esta PC. Abrilo y esperá a que detecte el token.',
    origenBloqueado: 'SIGIF Firma Local está abierto, pero no acepta pedidos desde esta dirección del sistema.',
    sinToken: 'SIGIF Firma Local no detecta el token USB. Conectalo y esperá unos segundos.',
};

async function consultarEstado() {
    try {
        const resp = await fetch(`${FIRMA_SERVICE_URL}/status`, { signal: AbortSignal.timeout(2000) });
        if (!resp.ok) return { disponible: false, tokens: 0, origenBloqueado: false };
        const data = await resp.json().catch(() => ({}));
        return { disponible: true, tokens: data.tokensDetectados ?? 0, origenBloqueado: false };
    } catch (err) {
        // "Failed to fetch" también es el error de CORS: si responde en no-cors, el cliente está
        // abierto pero no autoriza este origen (AllowedOriginsExtra), y firmar fallaría igual.
        if (err instanceof TypeError) {
            try {
                await fetch(`${FIRMA_SERVICE_URL}/status`, { mode: 'no-cors', signal: AbortSignal.timeout(2000) });
                return { disponible: false, tokens: 0, origenBloqueado: true };
            } catch { /* cerrado */ }
        }
        return { disponible: false, tokens: 0, origenBloqueado: false };
    }
}

export default function useServicioFirma() {
    const [estado, setEstado] = useState({ disponible: null, tokens: 0, origenBloqueado: false }); // null = verificando

    const verificar = useCallback(async () => {
        const nuevo = await consultarEstado();
        setEstado(nuevo);
        return nuevo;
    }, []);

    useEffect(() => {
        verificar();
        const t = setInterval(verificar, 5000);
        return () => clearInterval(t);
    }, [verificar]);

    const listo = estado.disponible === true && estado.tokens > 0;
    const mensaje = estado.disponible === null ? MENSAJES.verificando
        : estado.origenBloqueado ? MENSAJES.origenBloqueado
        : !estado.disponible ? MENSAJES.sinServicio
        : !listo ? MENSAJES.sinToken
        : null;
    return { ...estado, listo, mensaje, verificar };
}

/**
 * fetch al cliente para firmar. Si no se lo puede alcanzar (se cerró a mitad del lote), el
 * error sale marcado `cortar` para que el lote se detenga en vez de fallar fila por fila.
 */
export async function fetchFirmaLocal(path, options) {
    try {
        return await fetch(`${FIRMA_SERVICE_URL}${path}`, options);
    } catch {
        const e = new Error(MENSAJES.sinServicio);
        e.cortar = true;
        throw e;
    }
}
